import { z } from 'zod';
import { Check, Copy, ExternalLink, X } from 'lucide-react';
import { Alert } from './Alert';
import { Button } from './Button';
import { Card, CardContent, CardHeader, CardTitle } from './Card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './Dialog';
import { DomainStatusBadge, type DomainStatus } from './DomainStatusBadge';
import { Label } from './Field';
import { Form, FormField, type ZodForm } from './Form';
import { Input } from './Input';
import { Inline, Stack } from './Layout';
import { RegistrarSetup } from './RegistrarSetup';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './Sheet';
import { Text, TextLink } from './Text';

export const customDomainSchema = z.object({
  domain: z
    .string()
    .trim()
    .min(1, 'Enter a domain name.')
    .regex(
      /^[a-z0-9-]+(\.[a-z0-9-]+)+$/i,
      'Enter a domain like yourcompany.com — no http:// and no path.'
    ),
});

export type CustomDomainValues = z.infer<typeof customDomainSchema>;

export interface CustomDomainDialogViewProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The site's default address, absolute. Absent until first publish. */
  defaultUrl?: string;
  /** The default address was just copied: the copy button shows a tick. */
  defaultUrlCopied?: boolean;
  onCopyDefaultUrl: () => void;
  /** From `useZodForm(customDomainSchema, …)`. */
  form: ZodForm<typeof customDomainSchema>;
  /** A domain is already on the account: the box locks and the badge shows. */
  hasExistingDomain: boolean;
  status: DomainStatus | null;
  /**
   * Bought through Relay: we wrote its DNS, so there is nothing to set up.
   * Otherwise the registrar walkthrough shows until the domain is active.
   */
  boughtHere: boolean;
  /** Where the DNS walkthrough's "book a call" link goes. */
  scheduleUrl: string;
  adding: boolean;
  removing: boolean;
  verifying: boolean;
  onAddDomain: (domain: string) => void;
  /** The X beside the badge: asks first. */
  onRemoveDomain: () => void;
  onVerify: () => void;
  onClose: () => void;
  /** The "are you sure" over the dialog. */
  removeConfirmOpen: boolean;
  onRemoveConfirmOpenChange: (open: boolean) => void;
  onConfirmRemove: () => void;
}

/**
 * The custom-domain settings dialog: the site's Relay address with copy and
 * open, the domain box with Add or the status badge and remove, the state
 * messages, and the DNS walkthrough where the owner actually has records to
 * add. Verify sits in the footer until the domain is active.
 *
 *   <CustomDomainDialogView open onOpenChange={setOpen} form={form}
 *     hasExistingDomain={false} status={null}
 *     boughtHere={false} scheduleUrl={CAL_URL} adding={false} removing={false}
 *     verifying={false} onAddDomain={add} onRemoveDomain={askRemove}
 *     onVerify={verify} onClose={close} removeConfirmOpen={confirming}
 *     onRemoveConfirmOpenChange={setConfirming} onConfirmRemove={remove}
 *     onCopyDefaultUrl={copy} />
 */
export function CustomDomainDialogView({
  open,
  onOpenChange,
  defaultUrl,
  defaultUrlCopied = false,
  onCopyDefaultUrl,
  form,
  hasExistingDomain,
  status,
  boughtHere,
  scheduleUrl,
  adding,
  removing,
  verifying,
  onAddDomain,
  onRemoveDomain,
  onVerify,
  onClose,
  removeConfirmOpen,
  onRemoveConfirmOpenChange,
  onConfirmRemove,
}: CustomDomainDialogViewProps) {
  const domain = form.watch('domain');
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="xl" className="max-h-[90vh] overflow-y-auto w-full">
        <DialogHeader>
          <DialogTitle>Custom Domain Settings</DialogTitle>
          <DialogDescription>
            Add a custom domain to serve your website at your own URL
          </DialogDescription>
        </DialogHeader>

        <Stack gap={6}>
          {/* Default Relay address — always available once the site is published. */}
          {defaultUrl && (
            <Stack gap={2}>
              <Label htmlFor="default-url">Your Relay address</Label>
              <Inline gap={2} align="center">
                <Input
                  id="default-url"
                  readOnly
                  value={defaultUrl}
                  onFocus={(e) => e.target.select()}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={onCopyDefaultUrl}
                  className="shrink-0"
                  aria-label={
                    defaultUrlCopied ? 'Copied' : 'Copy your Relay address'
                  }
                  title={defaultUrlCopied ? 'Copied' : 'Copy URL'}
                >
                  {defaultUrlCopied ? (
                    <Check className="size-icon-sm text-success" />
                  ) : (
                    <Copy className="size-icon-sm" />
                  )}
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="shrink-0"
                  aria-label="Open your Relay address in a new tab"
                  title="Open site"
                  href={defaultUrl}
                  external
                >
                  <ExternalLink className="size-icon-sm" />
                </Button>
              </Inline>
              <Text variant="body-sm" tone="muted">
                This is your free Relay web address. Add a custom domain below
                to use your own.
              </Text>
            </Stack>
          )}

          {/* Domain Input Section */}
          <Stack gap={4}>
            <Form
              form={form}
              gap={2}
              onSubmit={(values) => onAddDomain(values.domain)}
            >
              <Inline gap={2} align="start">
                <Stack gap={0} grow>
                  <FormField form={form} name="domain" label="Domain Name">
                    {(field) => (
                      <Input
                        {...field}
                        placeholder="yourcompany.com"
                        disabled={adding || removing || hasExistingDomain}
                      />
                    )}
                  </FormField>
                </Stack>

                {!hasExistingDomain ? (
                  <Button type="submit" disabled={adding} className="shrink-0">
                    {adding ? 'Adding...' : 'Add Domain'}
                  </Button>
                ) : (
                  <>
                    <DomainStatusBadge status={status} />
                    <Button
                      variant="destructive-ghost"
                      size="icon"
                      onClick={onRemoveDomain}
                      disabled={removing}
                      className="shrink-0"
                      title="Remove domain"
                      aria-label="Remove domain"
                    >
                      <X className="size-icon-md" />
                    </Button>
                  </>
                )}
              </Inline>
            </Form>

            {hasExistingDomain && (
              <Text variant="body-sm" tone="muted">
                To change your domain, first remove the existing one.
              </Text>
            )}
          </Stack>

          {/* Status Messages */}
          {status === 'ACTIVE' && (
            <Alert tone="success" title="Domain Active!">
              Your website is now accessible at{' '}
              <TextLink href={`https://${domain}`} external tone="inherit">
                {domain} <ExternalLink className="inline size-icon-xs" />
              </TextLink>
            </Alert>
          )}

          {status === 'FAILED' && (
            <Alert tone="destructive" title="Domain Verification Failed">
              Please check your DNS settings and try verifying again.
            </Alert>
          )}

          {/* Bought through Relay: we registered it and wrote its DNS records
              at the registrar during checkout. Showing the setup walkthrough
              here told owners to go and do, by hand, the thing we had already
              done — at a registrar they may not even have an account with. */}
          {domain && boughtHere && status !== 'ACTIVE' && (
            <Alert tone="info" title="We're setting this up for you">
              Relay registered {domain} and pointed it at your site. It can take
              up to an hour for the address to work everywhere. Nothing for you
              to do.
            </Alert>
          )}

          {/* DNS Instructions — only where the owner actually has records to
              add: a domain from another registrar that is not reaching us yet. */}
          {domain && !boughtHere && status !== 'ACTIVE' && (
            <Card>
              <CardHeader>
                <CardTitle>DNS Configuration</CardTitle>
              </CardHeader>
              <CardContent>
                <RegistrarSetup scheduleUrl={scheduleUrl} />
              </CardContent>
            </Card>
          )}
        </Stack>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>

          {domain && status && status !== 'ACTIVE' && (
            <Button onClick={onVerify} disabled={verifying}>
              {verifying ? 'Verifying...' : 'Verify'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>

      <AlertDialog
        open={removeConfirmOpen}
        onOpenChange={onRemoveConfirmOpenChange}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Domain</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove your domain {domain}?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => onRemoveConfirmOpenChange(false)}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction onClick={onConfirmRemove}>
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
}
